import React, { useEffect, useState } from 'react'
import { Plus, Trash2, X } from 'lucide-react'
import { DynamicActivityDefinition, WhileActivitySettings, Variable } from '../types'
import { ACTIVITY_TYPES } from '../helpers'

interface AdvancedWhileSettingsProps {
  selectedStep?: any
  showSettings: boolean
  onSave: (parameters: any, variables: Variable[]) => void
  onCancel: () => void
  workflowVariables?: Variable[]
}

const AdvancedWhileSettings: React.FC<AdvancedWhileSettingsProps> = ({
  selectedStep,
  showSettings,
  onSave,
  onCancel,
  workflowVariables = []
}) => {
  const [condition, setCondition] = useState(selectedStep?.settings?.condition || '')
  const [conditionType, setConditionType] = useState(selectedStep?.settings?.conditionType || 'Variable')
  const [bodyActivities, setBodyActivities] = useState<DynamicActivityDefinition[]>(
    selectedStep?.settings?.bodyActivities || []
  )

  useEffect(() => {
    if (selectedStep) {
      setCondition(selectedStep.settings?.condition || '')
      setConditionType(selectedStep.settings?.conditionType || 'Variable')
      setBodyActivities(selectedStep.settings?.bodyActivities || [])
    }
  }, [selectedStep])

  if (!showSettings || !selectedStep) return null

  const addActivity = () => {
    const newActivity: DynamicActivityDefinition = {
      type: ACTIVITY_TYPES.WRITE_LINE,
      name: `Body Activity ${bodyActivities.length + 1}`,
      parameters: { message: `Loop iteration message` }
    }

    setBodyActivities([...bodyActivities, newActivity])
  }

  const removeActivity = (index: number) => {
    setBodyActivities(bodyActivities.filter((_, i) => i !== index))
  }

  const updateActivity = (index: number, field: string, value: any) => {
    const updated = [...bodyActivities]
    if (field === 'type') {
      updated[index].type = value
      // Reset parameters and update name when type changes
      updated[index].parameters = getDefaultParameters(value)
      updated[index].name = getDefaultActivityName(value, index + 1)
    } else if (field === 'name') {
      updated[index].name = value
    } else {
      updated[index].parameters = { ...updated[index].parameters, [field]: value }
    }
    setBodyActivities(updated)
  }

  const getDefaultParameters = (activityType: string) => {
    switch (activityType) {
      case ACTIVITY_TYPES.WRITE_LINE:
        return { message: 'Loop iteration message' }
      case ACTIVITY_TYPES.DELAY:
        return { duration: '00:00:05' }
      case ACTIVITY_TYPES.SEND_EMAIL_MARKETING:
        return { recipientEmail: '', recipientName: '' }
      case ACTIVITY_TYPES.SET_VARIABLE:
        return { variable: '', value: '' }
      case ACTIVITY_TYPES.FLOW_SEND_HTTP_REQUEST:
        return { url: '', method: 'GET', content: '{}', contentType: 'application/json' }
      default:
        return {}
    }
  }

  const getDefaultActivityName = (activityType: string, index: number) => {
    const typeNames: { [key: string]: string } = {
      [ACTIVITY_TYPES.WRITE_LINE]: 'WriteLine',
      [ACTIVITY_TYPES.DELAY]: 'Delay',
      [ACTIVITY_TYPES.SEND_EMAIL_MARKETING]: 'SendEmail',
      [ACTIVITY_TYPES.SET_VARIABLE]: 'SetVariable',
      [ACTIVITY_TYPES.FLOW_SEND_HTTP_REQUEST]: 'HttpRequest',
      [ACTIVITY_TYPES.BREAK]: 'Break',
    }

    const baseName = typeNames[activityType] || 'Activity'
    return `${baseName} ${index}`
  }

  const handleSave = () => {
    const parameters: WhileActivitySettings = {
      condition,
      conditionType: conditionType as 'Literal' | 'Variable' | 'Expression',
      bodyActivities: bodyActivities.length > 0 ? bodyActivities : undefined,
    }

    onSave(parameters, []) // No variables managed here anymore
  }

  const renderActivityForm = (activity: DynamicActivityDefinition, index: number) => (
    <div key={index} className="border border-slate-200 dark:border-zink-600 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex-1 grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
              Activity Type
            </label>
            <select
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
              value={activity.type}
              onChange={(e) => updateActivity(index, 'type', e.target.value)}
            >
              <option value={ACTIVITY_TYPES.WRITE_LINE}>Write Line</option>
              <option value={ACTIVITY_TYPES.DELAY}>Delay</option>
              <option value={ACTIVITY_TYPES.SEND_EMAIL_MARKETING}>Send Email Marketing</option>
              <option value={ACTIVITY_TYPES.SET_VARIABLE}>Set Variable</option>
              <option value={ACTIVITY_TYPES.FLOW_SEND_HTTP_REQUEST}>HTTP Request</option>
              <option value={ACTIVITY_TYPES.BREAK}>Break</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
              Name
            </label>
            <input
              type="text"
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
              value={activity.name || ''}
              onChange={(e) => updateActivity(index, 'name', e.target.value)}
              placeholder="Activity name"
            />
          </div>
        </div>
        <button
          onClick={() => removeActivity(index)}
          className="ml-2 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Activity-specific parameters */}
      {activity.type === ACTIVITY_TYPES.WRITE_LINE && (
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
            Message
          </label>
          <textarea
            className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
            rows={2}
            value={activity.parameters?.message || ''}
            onChange={(e) => updateActivity(index, 'message', e.target.value)}
            placeholder="Enter message"
          />
        </div>
      )}

      {/* Delay Activity */}
      {activity.type === ACTIVITY_TYPES.DELAY && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
              Duration
            </label>
            <input
              type="text"
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
              value={activity.parameters?.duration || ''}
              onChange={(e) => updateActivity(index, 'duration', e.target.value)}
              placeholder="00:00:05"
            />
          </div>
        </div>
      )}

      {/* Send Email Marketing */}
      {activity.type === ACTIVITY_TYPES.SEND_EMAIL_MARKETING && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
              Recipient Email
            </label>
            <input
              type="email"
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
              value={activity.parameters?.recipientEmail || ''}
              onChange={(e) => updateActivity(index, 'recipientEmail', e.target.value)}
              placeholder="recipient@example.com"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
              Recipient Name
            </label>
            <input
              type="text"
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
              value={activity.parameters?.recipientName || ''}
              onChange={(e) => updateActivity(index, 'recipientName', e.target.value)}
              placeholder="Recipient Name"
            />
          </div>
        </div>
      )}

      {/* Set Variable */}
      {activity.type === ACTIVITY_TYPES.SET_VARIABLE && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
              Variable
            </label>
            <select
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
              value={activity.parameters?.variable || ''}
              onChange={(e) => updateActivity(index, 'variable', e.target.value)}
            >
              <option value="">Select a variable</option>
              {workflowVariables.map((variable) => (
                <option key={variable.id} value={variable.name}>
                  {variable.name} ({variable.typeName})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
              Value
            </label>
            <input
              type="text"
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
              value={activity.parameters?.value || ''}
              onChange={(e) => updateActivity(index, 'value', e.target.value)}
              placeholder="Enter value"
            />
          </div>
        </div>
      )}

      {/* HTTP Request */}
      {activity.type === ACTIVITY_TYPES.FLOW_SEND_HTTP_REQUEST && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
                URL
              </label>
              <input
                type="url"
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                value={activity.parameters?.url || ''}
                onChange={(e) => updateActivity(index, 'url', e.target.value)}
                placeholder="https://api.example.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
                Method
              </label>
              <select
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                value={activity.parameters?.method || 'GET'}
                onChange={(e) => updateActivity(index, 'method', e.target.value)}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
              Parsed Content
            </label>
            <select
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
              value={activity.parameters?.parsedContent || ''}
              onChange={(e) => updateActivity(index, 'parsedContent', e.target.value)}
            >
              <option value="">Select a variable</option>
              {workflowVariables.map((variable) => (
                <option key={variable.id} value={variable.name}>
                  {variable.name} ({variable.typeName})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
              Content
            </label>
            <textarea
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
              rows={2}
              value={activity.parameters?.content || ''}
              onChange={(e) => updateActivity(index, 'content', e.target.value)}
              placeholder="Request body (JSON)"
            />
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center  z-[9999]">
      <div className="relative bg-white dark:bg-zink-700 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
        <div className="p-6 border-b border-slate-200 dark:border-zink-600">
          <h3 className="text-15 text-slate-800 dark:text-zink-50">
            Configure While Activity
          </h3>
        </div>
        <span onClick={onCancel} className='absolute top-4 right-4 hover:text-slate-600'><X /></span>

        <div className="p-6 space-y-6">
          {/* Condition for While Activity */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Condition
                </label>
                {conditionType === 'Variable' ? (
                  <select
                    className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                  >
                    <option value="">Select a variable</option>
                    {workflowVariables.map((variable) => (
                      <option key={variable.id} value={variable.name}>
                        {variable.name} ({variable.typeName})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                    placeholder={conditionType === 'Literal' ? 'true or false' : 'Enter expression'}
                  />
                )}
                <p className="text-xs text-slate-500 dark:text-zink-400 mt-1">
                  The loop will continue while this condition is true.
                  {conditionType === 'Variable' && 'Select a variable from the dropdown'}
                  {conditionType === 'Literal' && 'Enter true or false'}
                  {conditionType === 'Expression' && 'Enter a condition expression'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Condition Type
                </label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  value={conditionType}
                  onChange={(e) => setConditionType(e.target.value)}
                >
                  <option value="Variable">Variable</option>
                  <option value="Literal">Literal (true/false)</option>
                  <option value="Expression">Expression</option>
                </select>
              </div>
            </div>
          </div>

          {/* Body Activities */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm text-slate-700 dark:text-zink-200">Loop Body</h4>
              <button
                onClick={addActivity}
                className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-2 text-sm"
              >
                <Plus className="h-4 w-4" />
                Add Activity
              </button>
            </div>
            <div className="space-y-3">
              {bodyActivities.map((activity, index) => renderActivityForm(activity, index))}
              {bodyActivities.length === 0 && (
                <div className="text-center py-8 text-slate-500 dark:text-zink-400 border-2 border-dashed border-slate-300 dark:border-zink-600 rounded-lg">
                  No activities in loop body. Click "Add Activity" to add one.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-slate-200 dark:border-zink-600 sticky bottom-0 bg-white dark:bg-zink-700 z-10">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-slate-600 dark:text-zink-300 hover:text-slate-800 dark:hover:text-zink-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  )
}

export default AdvancedWhileSettings
